/**
 * Adaptateur de stockage IndexedDB pour la persistance Zustand.
 *
 * Remplace localStorage, pour deux raisons :
 *   - pas de quota de 5 Mo (les images base64 survivent au rechargement) ;
 *   - écritures asynchrones, donc pas de blocage du fil principal pendant un
 *     glissement ou un redimensionnement.
 *
 * Extrait de useLabelStore.js, où il vivait en propre, pour servir aussi à la
 * conversation du Studio I.A. Une seule base, une clé par magasin.
 */
const IDB_DB_NAME = 'labelforge-db';
const IDB_STORE_NAME = 'kv';
let _idbPromise = null;

function openIDB() {
    if (_idbPromise) return _idbPromise;
    _idbPromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB not available'));
            return;
        }
        const req = indexedDB.open(IDB_DB_NAME, 1);
        req.onupgradeneeded = () => {
            req.result.createObjectStore(IDB_STORE_NAME);
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return _idbPromise;
}

function withIDBStore(mode, op) {
    return openIDB().then((db) => new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE_NAME, mode);
        const store = tx.objectStore(IDB_STORE_NAME);
        const req = op(store);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    }));
}

// Migration unique localStorage → IndexedDB, à la première lecture.
// Le suivi est PAR CLÉ : un drapeau unique laisserait le premier magasin lu
// marquer la migration comme faite et priverait les suivants de la leur.
const _migres = new Set();
async function migrateFromLocalStorage(name) {
    if (_migres.has(name)) return;
    _migres.add(name);
    try {
        const legacy = localStorage.getItem(name);
        if (legacy) {
            const existing = await withIDBStore('readonly', (s) => s.get(name));
            if (!existing) {
                await withIDBStore('readwrite', (s) => s.put(legacy, name));
                // On retire la copie localStorage : la garder rejouerait le
                // quota qu'on cherche justement à fuir.
                try { localStorage.removeItem(name); } catch {}
            }
        }
    } catch (e) {
        console.warn('IDB migration from localStorage failed:', e);
    }
}

/**
 * Fabrique un adaptateur de stockage.
 *
 * `debounceMs` regroupe les écritures rapprochées en une seule.
 *
 * `objets` change la NATURE de ce qui transite. Par défaut, l'adaptateur reçoit
 * des chaînes : c'est ce qu'attend `createJSONStorage`, qui appelle
 * `JSON.stringify` **avant** de nous les passer. Or ce `stringify` est
 * synchrone, et Zustand le déclenche à CHAQUE `set()` — le regroupement plus bas
 * n'épargne que l'écriture dans IndexedDB, jamais la sérialisation. Sur une
 * conversation de 12 Mo (quatorze images de référence en base64), cela coûtait
 * 8,3 ms de fil principal par caractère tapé, pour un résultat jeté 400 ms plus
 * tard. C'est la cause des ralentissements à la frappe signalés le 2026-08-14.
 *
 * Avec `objets: true`, on se passe de `createJSONStorage` : l'objet arrive tel
 * quel et c'est IndexedDB qui le sérialise, par clonage structuré, une seule
 * fois par salve. Le coût par frappe tombe à la conservation d'une référence.
 *
 * L'écriture en attente est vidée quand l'onglet part (`pagehide`), sinon les
 * dernières frappes seraient perdues — c'est exactement le cas que la
 * persistance doit couvrir.
 */
export function createIdbStorage({ debounceMs = 0, objets = false } = {}) {
    let timer = null;
    let enAttente = null; // { name, value }

    const ecrire = async (name, value) => {
        try {
            await withIDBStore('readwrite', (s) => s.put(value, name));
        } catch (e) {
            // Le clonage structuré REFUSE ce que `JSON.stringify` laissait
            // passer en silence (une fonction, un Symbol, un nœud du DOM). On
            // retombe alors sur une chaîne JSON plutôt que de cesser
            // d'enregistrer : `getItem` sait relire les deux formes.
            if (objets) {
                try {
                    await withIDBStore('readwrite', (s) => s.put(JSON.stringify(value), name));
                    return;
                } catch (e2) {
                    console.warn('IDB setItem failed (repli JSON):', e2);
                    return;
                }
            }
            console.warn('IDB setItem failed:', e);
        }
    };

    const vider = () => {
        if (timer) { clearTimeout(timer); timer = null; }
        if (enAttente) {
            const { name, value } = enAttente;
            enAttente = null;
            ecrire(name, value);
        }
    };

    if (debounceMs > 0 && typeof window !== 'undefined') {
        // `pagehide` plutôt que `beforeunload` : c'est le seul des deux que
        // Safari mobile déclenche à coup sûr.
        window.addEventListener('pagehide', vider);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') vider();
        });
    }

    return {
        getItem: async (name) => {
            await migrateFromLocalStorage(name);
            try {
                const val = await withIDBStore('readonly', (s) => s.get(name));
                if (val == null) return null;
                // En mode objet, une CHAÎNE trouvée en base vient d'une version
                // antérieure (qui passait par `createJSONStorage`) ou du repli
                // ci-dessus. La relire évite de perdre la conversation en cours
                // au premier chargement de la nouvelle version.
                if (objets && typeof val === 'string') {
                    try { return JSON.parse(val); } catch { return null; }
                }
                return val;
            } catch (e) {
                console.warn('IDB getItem failed:', e);
                return null;
            }
        },
        setItem: async (name, value) => {
            if (debounceMs <= 0) return ecrire(name, value);
            enAttente = { name, value };
            if (timer) clearTimeout(timer);
            timer = setTimeout(vider, debounceMs);
        },
        removeItem: async (name) => {
            if (enAttente && enAttente.name === name) {
                enAttente = null;
                if (timer) { clearTimeout(timer); timer = null; }
            }
            try {
                await withIDBStore('readwrite', (s) => s.delete(name));
            } catch (e) {
                console.warn('IDB removeItem failed:', e);
            }
        },
    };
}
